import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * <admira-product-drawer> — the one right-hand drawer of the Interactive assets
 * page (Figma 768:1748), rendered once from the layout by
 * `snippets/admira-product-drawer.liquid`. It shows three bodies:
 *
 *   - **Adăugare rapidă** (768:5394). A card's "Vezi dimensiunile" link
 *     (`[data-admira-quick-add]`) opens it. The body is
 *     `sections/admira-quick-add.liquid`, fetched through the Section Rendering
 *     API on the product's own URL and cached per product for the page's life,
 *     with the `<style data-section-stylesheet>` the response carries.
 *   - **Solicită ofertă** (768:7208) and **Solicită publicarea în SEAP**
 *     (768:6677). A B2B control (`[data-b2b-open]`) opens them, from the PDP
 *     buy box or from inside the quick-add view. The body is cloned from the
 *     panel's inert `<template>`, so a page nobody requests from never parses a
 *     form (§5.1.2).
 *
 * Opening, closing, the focus trap, Esc, scroll locking, outside-click close
 * and the closing animation are all `DialogComponent`'s, unchanged.
 *
 * Every trigger is a real link. A modified click — new tab, new window — is
 * left alone, and with no JavaScript the links go where they say: the product
 * page, or the contact page.
 *
 * @extends {DialogComponent}
 */
class AdmiraProductDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'title', 'body'];

  /** @type {Map<string, Promise<string>>} */
  static #quickAddCache = new Map();

  /** The control outside the drawer that opened it, for focus on close. */
  /** @type {HTMLElement | null} */
  #returnFocus = null;

  /** Guards against a slow fetch overwriting a view opened after it. */
  #requestToken = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
    this.refs.dialog.addEventListener('close', this.#onClose);
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#revealServerResult();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    this.refs.dialog.removeEventListener('close', this.#onClose);
    this.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.defaultPrevented || !(event.target instanceof Element)) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const quickAdd = event.target.closest('[data-admira-quick-add]');
    if (quickAdd instanceof HTMLElement && quickAdd.dataset.admiraQuickAdd) {
      event.preventDefault();
      this.openQuickAdd(quickAdd.dataset.admiraQuickAdd, quickAdd);
      return;
    }

    const request = event.target.closest('[data-b2b-open]');
    const panel = request?.closest('[data-admira-b2b]');
    if (request instanceof HTMLElement && panel instanceof HTMLElement) {
      event.preventDefault();
      this.openRequest(panel, request.dataset.b2bOpen ?? 'quote', request);
    }
  };

  /**
   * Opens the quick-add view for a product.
   * @param {string} productUrl - The product's URL, as the card links to it.
   * @param {HTMLElement} [trigger]
   */
  async openQuickAdd(productUrl, trigger) {
    const token = ++this.#requestToken;
    const title = this.dataset.quickAddTitle ?? '';
    this.#rememberTrigger(trigger);
    this.#render(title, this.#statusNode(this.dataset.loading ?? ''));
    this.#setBusy(true);
    this.showDialog();

    try {
      const [html] = await Promise.all([this.#fetchQuickAdd(productUrl), this.#load(this.dataset.quickAddSrc)]);
      if (token !== this.#requestToken) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-admira-quick-add-content]');
      if (!content) throw new Error('the quick-add section rendered nothing');

      // The section's own CSS arrives in the response, subsetted to its render
      // tree — the host page never rendered it, so it is mounted with the view.
      // Horizon's section-renderer.js does the same for its fetched sections.
      const view = document.createDocumentFragment();
      const styles = parsed.querySelector('style[data-section-stylesheet]');
      if (styles) view.append(document.importNode(styles, true));
      view.append(document.importNode(content, true));

      this.#render(title, view);
    } catch (error) {
      console.error('admira-product-drawer:', error);
      AdmiraProductDrawer.#quickAddCache.delete(this.#quickAddUrl(productUrl));
      if (token === this.#requestToken) this.#render(title, this.#errorNode(productUrl));
    } finally {
      if (token === this.#requestToken) this.#setBusy(false);
    }
  }

  /**
   * Opens one of a B2B panel's requests.
   * @param {HTMLElement} panel - The `[data-admira-b2b]` panel.
   * @param {string} intent - 'quote' or 'seap'.
   * @param {HTMLElement} [trigger]
   */
  async openRequest(panel, intent, trigger) {
    const template = panel.querySelector(`template[data-b2b-template="${intent}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;

    const token = ++this.#requestToken;
    // Clone before anything replaces the body: when the request is opened from
    // inside the quick-add view, the panel is about to be removed with it.
    const form = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    this.#applyVariant(panel, form);
    this.#rememberTrigger(trigger);

    // Opened from the quick-add view, the form's CSS came in that view's section
    // stylesheet — keep it mounted when the view is replaced.
    if (this.refs.body.contains(panel)) {
      const styles = this.refs.body.querySelector(':scope > style[data-section-stylesheet]');
      if (styles) form.prepend(styles);
    }

    await this.#load(this.dataset.requestFormSrc);
    if (token !== this.#requestToken) return;

    this.#render(template.dataset.b2bTitle ?? '', form);
    this.showDialog();
  }

  /**
   * The template renders the variant that was current when the page loaded.
   * Correct the summary, SKU and link to the one chosen since — on the PDP
   * that is the buy box's form, in the drawer the quick-add form.
   * @param {HTMLElement} panel
   * @param {DocumentFragment} form
   */
  #applyVariant(panel, form) {
    const data = panel.querySelector('script[data-b2b-variants]');
    if (!data) return;

    const scope = panel.closest('[data-admira-quick-add-content]') ?? panel.closest('.shopify-section') ?? document;
    const productId = panel.dataset.productId;
    const input = scope.querySelector(`product-form-component[data-product-id="${productId}"] input[name="id"]`);
    if (!(input instanceof HTMLInputElement) || !input.value) return;

    /** @type {Array<{id: number, title: string, sku: string | null, url: string, image: string | null}>} */
    let variants = [];
    try {
      variants = JSON.parse(data.textContent ?? '[]');
    } catch {
      return;
    }

    const variant = variants.find((entry) => String(entry.id) === input.value);
    if (!variant) return;

    const text = form.querySelector('[data-b2b-variant-text]');
    if (text instanceof HTMLElement) {
      text.textContent = variant.title;
      text.hidden = false;
    }

    /** @type {Array<[string, string | null]>} */
    const fields = [
      ['[data-b2b-variant-field]', variant.title],
      ['[data-b2b-sku-field]', variant.sku],
      ['[data-b2b-url-field]', variant.url],
    ];
    for (const [selector, value] of fields) {
      const field = form.querySelector(selector);
      if (field instanceof HTMLInputElement) field.value = value ?? '';
    }

    const image = form.querySelector('[data-b2b-image]');
    if (image instanceof HTMLImageElement && variant.image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
  }

  /**
   * After a full-page round trip — Shopify's spam challenge, or a submission
   * with scripting off — the outcome is rendered into the panel's template,
   * where nobody would see it. Open the drawer on it instead.
   */
  #revealServerResult() {
    for (const template of document.querySelectorAll('template[data-b2b-template]')) {
      if (!(template instanceof HTMLTemplateElement)) continue;
      if (!template.content.querySelector('[data-request-form-result]')) continue;

      const panel = template.closest('[data-admira-b2b]');
      if (panel instanceof HTMLElement) this.openRequest(panel, template.dataset.b2bTemplate ?? 'quote');
      return;
    }
  }

  /**
   * An add from inside the drawer closes it once the cart has accepted it, so
   * the cart drawer that opens next is not stacked under a second modal.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!this.refs.dialog.open || !(event.target instanceof Node) || !this.refs.dialog.contains(event.target)) return;

    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    if (promise instanceof Promise) {
      promise.then(() => this.closeDialog()).catch(() => {});
    }
  };

  #onClose = () => {
    const target = this.#returnFocus;
    this.#returnFocus = null;
    this.#requestToken++;
    this.refs.body.replaceChildren();

    // The browser returns focus to whatever opened a modal — unless that was
    // inside the drawer's own body, which has just been emptied.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (target?.isConnected && (!active || active === document.body)) target.focus();
    });
  };

  /** @param {HTMLElement | undefined} trigger */
  #rememberTrigger(trigger) {
    if (trigger && !this.refs.dialog.contains(trigger)) this.#returnFocus = trigger;
  }

  /**
   * @param {string} title
   * @param {Node} node
   */
  #render(title, node) {
    this.refs.title.textContent = title;
    this.refs.body.replaceChildren(node);
    this.refs.body.scrollTop = 0;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.refs.body.toggleAttribute('aria-busy', busy);
  }

  /** @param {string} productUrl */
  #quickAddUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', this.dataset.quickAddSection ?? 'admira-quick-add');
    return url.toString();
  }

  /**
   * @param {string} productUrl
   * @returns {Promise<string>}
   */
  #fetchQuickAdd(productUrl) {
    const url = this.#quickAddUrl(productUrl);
    let pending = AdmiraProductDrawer.#quickAddCache.get(url);
    if (!pending) {
      pending = fetch(url, { headers: { Accept: 'text/html' } }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      });
      AdmiraProductDrawer.#quickAddCache.set(url, pending);
    }
    return pending;
  }

  /**
   * Loads a module the drawer's body needs. A fetched section's own
   * `<script>` tags never run, so the drawer brings them.
   * @param {string | undefined} src
   */
  async #load(src) {
    if (src) await import(src);
  }

  /** @param {string} message */
  #statusNode(message) {
    const status = document.createElement('p');
    status.className = 'admira-drawer__status';
    status.setAttribute('role', 'status');
    status.textContent = message;
    return status;
  }

  /** @param {string} productUrl */
  #errorNode(productUrl) {
    const status = this.#statusNode(this.dataset.error ?? '');
    status.setAttribute('role', 'alert');
    const link = document.createElement('a');
    link.href = productUrl;
    link.textContent = this.dataset.errorLink ?? '';
    status.append(' ', link);
    return status;
  }
}

if (!customElements.get('admira-product-drawer')) {
  customElements.define('admira-product-drawer', AdmiraProductDrawer);
}
